/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ClientKeySecretDialog Component
 * ----------------------
 * Modal dialog component for displaying and managing API client credentials.
 * 
 * Features:
 * - Shows Client Key and Client Secret with secure masking.
 * - Allows one-time reveal of client secret.
 * - Supports clipboard copy actions for both key and secret.
 * - Automatically hides when `open` is false.
 * - Themed styling via ThemeContext.
 *
 * Props:
 *  - open (boolean): Controls dialog visibility.
 *  - onClose (function): Handler to close the dialog.
 *  - clientKey (string): The API client key.
 *  - clientSecret (string): The API client secret.
 *  - actionLabel (string): Label for the close button.
 */

import React, { useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";

export default function ClientKeySecretDialog({
    open,
    onClose,
    clientKey,
    clientSecret,
    actionLabel = "Close",
}) {
    const { theme } = useTheme();

    const [secretRevealed, setSecretRevealed] = useState(false);
    const [copiedKey, setCopiedKey] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);

    useEffect(() => {
        if (open) {
            setSecretRevealed(false);
            setCopiedKey(false);
            setCopiedSecret(false);
        }
    }, [open]);

    async function handleCopy(text, setCopied) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }

    function handleRevealSecret() {
        setSecretRevealed(true);
    }

    if (!open) return null;

    const hasValidClientKey = clientKey && clientKey.trim() !== "";
    const hasValidClientSecret = clientSecret && clientSecret.trim() !== "";

    // Determine clientSecret display value
    const secretValue = hasValidClientSecret
        ? (secretRevealed ? clientSecret : "********")
        : "";

    // Determine copy button state
    const canCopySecret = secretRevealed && hasValidClientSecret;

    return (
        <div
            style={{
                position: "fixed",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                background: "rgba(30,40,60,0.16)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
            }}
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
        >
            <div
                style={{
                    minWidth: 340,
                    maxWidth: "97vw",
                    background: "#fff",
                    borderRadius: theme.cardRadius,
                    boxShadow: "0 8px 32px rgba(0,0,0,.14)",
                    border: "3px solid transparent",
                    backgroundClip: "padding-box, border-box",
                    backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                    backgroundOrigin: "border-box",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "22px 26px 10px 26px",
                        borderTopLeftRadius: theme.cardRadius,
                        borderTopRightRadius: theme.cardRadius,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: 19,
                            letterSpacing: ".01em",
                            color: theme.primary,
                        }}
                    >
                        API Credentials
                    </span>
                    <button
                        onClick={onClose}
                        style={{
                            border: "none",
                            background: "none",
                            fontSize: 22,
                            color: theme.secondary,
                            cursor: "pointer",
                        }}
                        title="Close"
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: "18px 26px 12px 26px" }}>
                    {/* Client Key */}
                    <div style={{ marginBottom: 18 }}>
                        <label
                            style={{
                                fontWeight: 600,
                                fontSize: 15,
                                color: theme.primary,
                            }}
                        >
                            Client Key
                        </label>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginTop: 5,
                            }}
                        >
                            <input
                                type="text"
                                value={clientKey}
                                readOnly
                                style={{
                                    fontSize: 16,
                                    fontFamily: "monospace",
                                    background: "#f6f8fa",
                                    color: theme.text,
                                    border: `1.4px solid ${theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "7px 10px",
                                    width: "100%",
                                    letterSpacing: ".02em",
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => handleCopy(clientKey, setCopiedKey)}
                                disabled={!hasValidClientKey}
                                style={{
                                    background: theme.secondary,
                                    color: "#fff",
                                    fontWeight: 600,
                                    borderRadius: theme.buttonRadius,
                                    border: "none",
                                    fontSize: 15,
                                    padding: "7px 12px",
                                    cursor: hasValidClientKey ? "pointer" : "not-allowed",
                                    boxShadow: "0 2px 5px rgba(30,58,138,0.07)",
                                    opacity: hasValidClientKey ? 1 : 0.5,
                                }}
                                title={hasValidClientKey ? "Copy" : "No key to Copy"}
                            >
                                {copiedKey ? <i className="bi bi-check-lg" /> : <i className="bi bi-clipboard" />}
                            </button>

                        </div>
                    </div>

                    {/* Client Secret */}
                    <div style={{ marginBottom: 18 }}>
                        <label
                            style={{
                                fontWeight: 600,
                                fontSize: 15,
                                color: theme.primary,
                            }}
                        >
                            Client Secret
                        </label>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginTop: 5,
                            }}
                        >
                            <input
                                type={secretRevealed ? "text" : "password"}
                                value={secretValue}
                                readOnly
                                style={{
                                    fontSize: 16,
                                    fontFamily: "monospace",
                                    background: "#f6f8fa",
                                    color: theme.text,
                                    border: `1.4px solid ${theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "7px 10px",
                                    width: "100%",
                                    letterSpacing: ".03em",
                                }}
                            />
                            {!secretRevealed ? (
                                <button
                                    type="button"
                                    onClick={handleRevealSecret}
                                    style={{
                                        background: theme.secondary,
                                        color: "#fff",
                                        fontWeight: 600,
                                        borderRadius: theme.buttonRadius,
                                        border: "none",
                                        fontSize: 15,
                                        padding: "7px 12px",
                                        cursor: "pointer",
                                    }}
                                    title="Reveal Secret (shown only once)"
                                >
                                    <i className="bi bi-eye" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => handleCopy(clientSecret, setCopiedSecret)}
                                    disabled={!canCopySecret}
                                    style={{
                                        background: theme.secondary,
                                        color: "#fff",
                                        fontWeight: 600,
                                        borderRadius: theme.buttonRadius,
                                        border: "none",
                                        fontSize: 15,
                                        padding: "7px 12px",
                                        cursor: canCopySecret ? "pointer" : "not-allowed",
                                        opacity: canCopySecret ? 1 : 0.5,
                                    }}
                                    title={canCopySecret ? "Copy" : "No secret to copy"}
                                >
                                    {copiedSecret ? <i className="bi bi-check-lg" /> : <i className="bi bi-clipboard" />}
                                </button>
                            )}
                        </div>
                        <div style={{ color: theme.secondary, fontSize: 13, marginTop: 2, maxWidth: "25em" }}>
                            {clientSecret
                                ? (!secretRevealed
                                    ? "Secret will be visible only once. Please copy and store securely."
                                    : "Keep your secret safe. It will not be shown again.")
                                : "The client secret is not displayed for security reasons. To get a new client secret, please click Regenerate."}
                        </div>
                    </div>
                </div>

                {/* Info */}
                <div
                    style={{
                        marginBottom: "1em",
                        padding: "12px 26px 18px 26px",
                        maxWidth: "25em",
                    }}
                >
                    <span>These are OAuth2 Credentials and need to be configured in your application's .env file.</span>
                </div>

                {/* Buttons */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderTop: `1px solid #e0e3eb`,
                        padding: "12px 26px 18px 26px",
                        borderBottomLeftRadius: theme.cardRadius,
                        borderBottomRightRadius: theme.cardRadius,
                        background: "#f8fafc",
                    }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: theme.primary,
                            color: "#fff",
                            fontWeight: 600,
                            borderRadius: theme.buttonRadius,
                            border: "none",
                            fontSize: 15,
                            padding: "7px 26px",
                            cursor: "pointer",
                        }}
                    >
                        {actionLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
